import { useEffect, useRef, useCallback } from "react";

// Generic debounce hook that works with any function
function useDebounce(callback, delay = 800, dependencies = []) {
  // Store the callback in a ref
  const callbackRef = useRef(callback);

  // Store the timeout ID for cleanup
  const timeoutRef = useRef(null);

  // Update the callback ref when callback changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create a memoized debounced function
  const debouncedCallback = useCallback(
    (...args) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args);
      }, delay);
    },
    [delay]
  );

  // Clean up on unmount or when dependencies change
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [delay, ...dependencies]);

  return debouncedCallback;
}

export default useDebounce;
